/**
 * Subscription Feature Registry
 *
 * Maps every subscription feature_code from the database to its UI metadata
 * (title + description shown on the lock overlay).
 *
 * IMPORTANT: This file is the single source of truth for lock-screen copy.
 * - When a new feature is seeded to the DB, add its code here for tailored messaging.
 * - If a code is NOT in this registry the system still works — it generates a
 *   title from the code itself and shows a generic description automatically.
 */

export interface FeatureMetadata {
  title: string;
  description: string;
}

export const FEATURE_REGISTRY: Record<string, FeatureMetadata> = {
  // ── Library ────────────────────────────────────────────────────────────────
  library_management: {
    title: 'Library Management',
    description:
      "Upgrade to manage your school's library, catalogue books, and handle circulation.",
  },
  library_book_limit: {
    title: 'Book Management',
    description: 'Upgrade to add and manage an unlimited number of books in your library.',
  },
  library_circulation: {
    title: 'Book Circulation',
    description: 'Upgrade to issue, return, and track book circulation for students.',
  },

  // ── Academic ───────────────────────────────────────────────────────────────
  exam_management: {
    title: 'Exam Management',
    description: 'Upgrade to create exams, manage scorecards, and release results to students.',
  },
  scorecard_release_type: {
    title: 'Scorecard Release',
    description: 'Upgrade to automate scorecard release to students and parents.',
  },
  grade_book: {
    title: 'Grade Book',
    description:
      'Upgrade to maintain a full digital grade book and track academic performance over time.',
  },
  report_cards: {
    title: 'Report Cards',
    description: 'Upgrade to generate and distribute student report cards automatically.',
  },
  homework_assignments: {
    title: 'Homework & Assignments',
    description: 'Upgrade to assign homework, set due dates, and track student submissions.',
  },
  has_homework_module: {
    title: 'Homework Module',
    description: 'Upgrade to enable the homework module for your school.',
  },
  can_manage_timetable: {
    title: 'Timetable Management',
    description: 'Upgrade to build class schedules and manage timetables school-wide.',
  },
  can_manage_syllabus: {
    title: 'Syllabus Management',
    description: 'Upgrade to create, assign, and manage syllabi across all subjects.',
  },
  academic_year: {
    title: 'Academic Year',
    description: 'Upgrade to configure academic years and term dates for your school.',
  },
  can_manage_academics: {
    title: 'Academic Settings',
    description: 'Upgrade to configure academic settings and manage your curriculum.',
  },
  has_noncurrent_curriculum: {
    title: 'Non-Current Curriculum',
    description: 'Upgrade to access and manage non-current curriculum records.',
  },

  // ── Finance ────────────────────────────────────────────────────────────────
  fee_management: {
    title: 'Fee Management',
    description: 'Upgrade to manage fee templates, invoices, and student billing records.',
  },
  online_payment: {
    title: 'Online Payments',
    description: 'Upgrade to accept online payments and manage transactions digitally.',
  },
  payment_reminders: {
    title: 'Payment Reminders',
    description: 'Upgrade to automate payment reminder notifications for outstanding invoices.',
  },
  can_manage_finance: {
    title: 'Finance Settings',
    description: 'Upgrade to configure financial settings and payment gateways.',
  },

  // ── Communication ──────────────────────────────────────────────────────────
  parent_communication: {
    title: 'Parent Communication',
    description: 'Upgrade to enable direct messaging and communication with parents.',
  },
  sms_notifications: {
    title: 'SMS Notifications',
    description: 'Upgrade to send SMS alerts and notifications to parents and staff.',
  },
  email_notifications: {
    title: 'Email Notifications',
    description: 'Upgrade to send automated email notifications to parents and staff.',
  },
  announcements: {
    title: 'Announcements',
    description: 'Upgrade to post school-wide announcements to students and parents.',
  },
  teacher_chat_type: {
    title: 'Teacher Chat',
    description: 'Upgrade to enable advanced teacher-to-teacher and teacher-to-parent messaging.',
  },

  // ── Events ─────────────────────────────────────────────────────────────────
  event_creation_level: {
    title: 'Event Management',
    description: 'Upgrade to create and manage school events, collect RSVPs, and run paid events.',
  },
  event_survey: {
    title: 'Event Surveys',
    description: 'Upgrade to add RSVP surveys and collect responses for school events.',
  },
  paid_events: {
    title: 'Paid Events',
    description: 'Upgrade to create events with payment collection and manage registrations.',
  },

  // ── Awards ─────────────────────────────────────────────────────────────────
  award_issue_level: {
    title: 'Award Management',
    description: 'Upgrade to create and issue achievement awards to students.',
  },

  // ── Attendance ─────────────────────────────────────────────────────────────
  has_teacher_attendance: {
    title: 'Teacher Attendance',
    description: 'Upgrade to track and manage teacher attendance records.',
  },
  student_attendance_checkin: {
    title: 'Student Check-in / Check-out',
    description: "Upgrade to enable students' check-in and check-out tracking.",
  },
  attendance_tracking: {
    title: 'Attendance Tracking',
    description: 'Upgrade to access detailed attendance tracking and monitoring tools.',
  },
  attendance_reports: {
    title: 'Attendance Reports',
    description: 'Upgrade to generate and export detailed attendance reports.',
  },
  qr_code_attendance: {
    title: 'QR Code Attendance',
    description: 'Upgrade to enable QR code-based attendance marking for students.',
  },
  attendance_cutoff: {
    title: 'Attendance Cut-off',
    description: 'Upgrade to configure attendance cut-off times for your school.',
  },

  // ── Admissions ─────────────────────────────────────────────────────────────
  has_student_register_form: {
    title: 'Student Registration Form',
    description: 'Upgrade to create custom registration forms for student enrollment.',
  },
  bulk_student_import: {
    title: 'Bulk Import',
    description: 'Upgrade to import students, teachers, and other data in bulk from CSV files.',
  },
  can_manage_reenrolment: {
    title: 'Re-enrollment Management',
    description: 'Upgrade to manage re-enrollment cycles and track student re-enrollment status.',
  },
  student_profiles: {
    title: 'Student Profiles',
    description: 'Upgrade to access and manage detailed student profile information.',
  },

  // ── User Management ────────────────────────────────────────────────────────
  can_manage_roles: {
    title: 'Roles & User Management',
    description: 'Upgrade to create custom roles and manage user permissions.',
  },
  can_manage_users: {
    title: 'User Management',
    description: 'Upgrade to manage all users and their access across your school.',
  },

  // ── Advanced / Analytics ───────────────────────────────────────────────────
  analytics_dashboard: {
    title: 'Analytics Dashboard',
    description:
      'Upgrade to access detailed analytics and performance insights across your school.',
  },
  custom_reports: {
    title: 'Custom Reports',
    description: 'Upgrade to generate custom reports on financials, students, and teachers.',
  },
  reporting_level: {
    title: 'Advanced Analytics',
    description: 'Upgrade to unlock advanced analytics and reporting capabilities.',
  },
  api_access: {
    title: 'API Access',
    description: 'Upgrade to access the Classveew API for custom integrations.',
  },
  has_facility_manager: {
    title: 'Facility Management',
    description: 'Upgrade to manage school facilities, rooms, and resources.',
  },
  has_nurse_module: {
    title: 'Nurse Module',
    description: 'Upgrade to enable the health and nurse module for student health records.',
  },
  has_documentation_module: {
    title: 'Documentation Module',
    description: 'Upgrade to manage and store school documents digitally.',
  },
  dedicated_support: {
    title: 'Priority Support',
    description: 'Upgrade to access dedicated and 24/7 priority support.',
  },
};

/**
 * Resolves UI metadata for a given feature code.
 *
 * Falls back automatically if the code is not yet registered:
 *  - Title: generated from the code itself (snake_case → Title Case)
 *  - Description: generic upgrade prompt
 *
 * This means any new feature added to the database will automatically show
 * a lock overlay without requiring changes here — just add an entry above
 * for tailored copy.
 */
export function getFeatureMetadata(featureCode: string): FeatureMetadata {
  if (FEATURE_REGISTRY[featureCode]) {
    return FEATURE_REGISTRY[featureCode];
  }
  // Auto-generate title from snake_case feature code
  const autoTitle = featureCode
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  return {
    title: autoTitle,
    description:
      'Upgrade your plan to access this feature and unlock more capabilities for your school.',
  };
}
